import Vue from 'vue'
import Vuex from 'vuex'
import axios from '@/axios'
import app from './modules/app'

Vue.use(Vuex)

const loadSelectedArea = () => {
  try {
    const raw = localStorage.getItem('selectedArea');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (e) {
    return null;
  }
};

const loadSessionState = (key, fallback) => {
  try {
    const raw = sessionStorage.getItem(key)
    if (!raw) return fallback
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : fallback
  } catch (e) {
    return fallback
  }
}

const getAreaCode = (area) => {
  if (!area || typeof area !== 'object') return null
  return area.place_id || area.code || area.village_code || area?.address?.code || area?.address?.place_id || null
}

const chooseDefaultSelectedArea = (results, user) => {
  if (!Array.isArray(results) || results.length === 0) return null
  return results[0]
}

const normalizeAuthRegionResults = (results) => {
  return Array.isArray(results) ? results : []
}

const ensureSelectedAreaMatchesAuth = (state, commit, results) => {
  const normalizedResults = normalizeAuthRegionResults(results)
  const selectedAreaCode = getAreaCode(state.selectedArea)
  const validAreaCodes = new Set(normalizedResults.map(item => getAreaCode(item)).filter(Boolean))
  const isCurrentSelectedAreaValid = selectedAreaCode && validAreaCodes.has(selectedAreaCode)

  if (!isCurrentSelectedAreaValid) {
    const defaultArea = chooseDefaultSelectedArea(normalizedResults, state.user)
    commit('setSelectedArea', defaultArea)
  }
}

const clearSessionCaches = () => {
  try {
    sessionStorage.removeItem('insureApplyState')
    sessionStorage.removeItem('plotDrawState')
    sessionStorage.removeItem('policy-list-state')
  } catch (e) {
    // Ignore sessionStorage errors
  }
}

export default new Vuex.Store({
  modules: {
    app
  },
  state: {
    selectedArea: loadSelectedArea(),
    selectedYear: null,
    selectedCrop: null,
    authorizedArea: null,
    user: null,
    userRole: null,
    selectedLandPlots: [], // Add this new state property to store selected land plots
    codes: {}, // Add new state property to store code data by type
    // Central cache for authorized regions fetched from server
    authRegions: [],
    insureApplyState: loadSessionState('insureApplyState', null),
    plotDrawState: loadSessionState('plotDrawState', null)
  },
  mutations: {
    setSelectedArea (state, item) {
      state.selectedArea = item
      try {
        if (item) {
          localStorage.setItem('selectedArea', JSON.stringify(item))
        } else {
          localStorage.removeItem('selectedArea')
        }
      } catch (e) {
        // Ignore localStorage errors (e.g., private mode)
      }
    },
    setSelectedYear (state, year) {
      state.selectedYear = year
    },
    setSelectedCrop (state, crop) {
      state.selectedCrop = crop
    },
    setAuthorizedArea (state, area) {
      state.authorizedArea = area
    },
    setUser (state, user) {
      state.user = user
    },
    setUserRole (state, role) {
      state.userRole = role
    },
    // Add a new mutation to add a land plot to the selection
    addSelectedLandPlot (state, landPlot) {
      // Check if the plot is already in the selection to avoid duplicates
      const existingIndex = state.selectedLandPlots.findIndex(plot => plot.land_id === landPlot.land_id);
      if (existingIndex === -1) {
        state.selectedLandPlots.push(landPlot);
      }
    },
    // Add a mutation to clear selected land plots
    clearSelectedLandPlots (state) {
      state.selectedLandPlots = [];
    },
    // Add a mutation to update codes by type
    setCodes (state, { type, items }) {
      Vue.set(state.codes, type, items);
    },
    // Cache auth regions so multiple components can reuse them without extra requests
    setAuthRegions (state, regions) {
      state.authRegions = Array.isArray(regions) ? regions : [];
    },
    setInsureApplyState (state, payload) {
      state.insureApplyState = payload || null
      try {
        if (payload) {
          sessionStorage.setItem('insureApplyState', JSON.stringify(payload))
        } else {
          sessionStorage.removeItem('insureApplyState')
        }
      } catch (e) {
        // Ignore sessionStorage errors
      }
    },
    clearInsureApplyState (state) {
      state.insureApplyState = null
      try {
        sessionStorage.removeItem('insureApplyState')
      } catch (e) {
        // Ignore sessionStorage errors
      }
    },
    setPlotDrawState (state, payload) {
      state.plotDrawState = payload || null
      try {
        if (payload) {
          sessionStorage.setItem('plotDrawState', JSON.stringify(payload))
        } else {
          sessionStorage.removeItem('plotDrawState')
        }
      } catch (e) {
        // Ignore sessionStorage errors
      }
    },
    clearPlotDrawState (state) {
      state.plotDrawState = null
      try {
        sessionStorage.removeItem('plotDrawState')
      } catch (e) {
        // Ignore sessionStorage errors
      }
    },
    resetSessionState (state) {
      state.selectedArea = null
      state.selectedYear = null
      state.selectedCrop = null
      state.authorizedArea = []
      state.user = null
      state.userRole = null
      state.selectedLandPlots = []
      state.codes = {}
      state.authRegions = []
      state.insureApplyState = null
      state.plotDrawState = null

      try {
        localStorage.removeItem('selectedArea')
      } catch (e) {
        // Ignore localStorage errors
      }

      clearSessionCaches()
    }
  },
  getters: {
    isAdmin: state => state.userRole === 'admin',
    isNormalUser: state => state.userRole === 'user',
    // Add a getter to easily access codes by type
    getCodesByType: (state) => (type) => {
      return state.codes[type] || [];
    }
  },
  actions: {
    updateUser ({ commit }, user) {
      commit('setUser', user)
    },
    // Add a new action to add a land plot to selection
    addLandToSelection ({ commit }, landPlot) {
      commit('addSelectedLandPlot', landPlot);
    },
    // Updated action to query codes by type using axios
    async queryCodes ({ commit }, type) {
      try {
        const response = await axios.post('/codes/query_type', { type });
        commit('setCodes', { type, items: response.data.items });
        return response.data.items;
      } catch (error) {
        console.error(`Error fetching codes for type ${type}:`, error);
        return [];
      }
    },
    // Centralized action to fetch authorized regions used in multiple places
    async fetchAuthRegions ({ commit, state }) {
      // Return cached copy if already loaded
      if (state.authRegions && ((Array.isArray(state.authRegions) && state.authRegions.length > 0) || (typeof state.authRegions === 'object' && Object.keys(state.authRegions).length > 0))) {
        const cachedResults = normalizeAuthRegionResults(state.authorizedArea)
        ensureSelectedAreaMatchesAuth(state, commit, cachedResults)
        // return an object shaped like the server response for compatibility
        return {
          results: cachedResults,
          auth_region: state.authRegions
        };
      }

      try {
        const optionUrl = '/users/me/auth-region';
        const jwtToken = localStorage.getItem('jwt');
        const headers = {};
        if (jwtToken) headers.Authorization = `Bearer ${jwtToken}`;

        // The server in other places posts to this endpoint; keep POST for compatibility
        const resp = await axios.post(optionUrl, {}, { headers });
        const data = resp && resp.data ? resp.data : {};

        // commit both the 'results' list (authorizedArea) and the auth_region payload
        const results = normalizeAuthRegionResults(data.results)
        commit('setAuthorizedArea', results);
        commit('setAuthRegions', data.auth_region || []);

        ensureSelectedAreaMatchesAuth(state, commit, results)

        return data;
      } catch (err) {
        console.error('Error fetching auth regions:', err);
        commit('setAuthRegions', []);
        commit('setAuthorizedArea', []);
        return {};
      }
    },
    async login ({ commit }, { username, password }) {
      // Reset stale state from previous account before applying new login session
      commit('resetSessionState')

      const formData = new FormData();
      formData.append('username', username);
      formData.append('password', password);

      const response = await axios.post('/login/access-token', formData);
      const { access_token: accessToken } = response.data;

      localStorage.setItem('jwt', accessToken);
      return accessToken;
    },
    async fetchUser ({ commit }) {
      try {
        const response = await axios.get('/users/me');
        const user = response.data;
        commit('setUser', user);
        // 后端 /users/me 返回 role.role_code，缺失时回退到 type
        const roleCode = (user.role && user.role.role_code) || user.type || 'user';
        commit('setUserRole', user.is_superuser ? 'admin' : roleCode);
        return user;
      } catch (error) {
        // Token invalid
        localStorage.removeItem('jwt');
        commit('setUser', null);
        throw error;
      }
    },
    logout ({ commit }) {
      localStorage.removeItem('jwt');
      commit('resetSessionState');
    },
    /**
     * Fetch region geometry data from backend
     * @param {string} regionCode - The region code to fetch geometry for
     * @returns {Promise<Object>} GeoJSON FeatureCollection with region geometry
     */
    async fetchRegionGeometry ({ commit }, regionCode) {
      try {
        if (!regionCode) {
          console.warn('No region code provided to fetchRegionGeometry');
          return null;
        }

        const response = await axios.get('/region/geojson', {
          params: { code: regionCode }
        });

        return response.data;
      } catch (error) {
        console.error('Error fetching region geometry:', error);
        return null;
      }
    }
  }
})
